/**
 * ⚠️ 临时导航性能探针 —— 仅用于定位「点菜单后切换慢」，排查结束后整个文件删除。
 *
 * 页面加载后 window.__navPerf 一定存在，控制台随时可用，不需要刷新：
 *   __navPerf.enable()    开始采集（同时写入 localStorage，刷新后仍生效）
 *   __navPerf.disable()   停止采集
 *   __navPerf.report()    打印表格
 *   __navPerf.dump()      返回可整段复制的 JSON（含逐次提交与脚本归因明细）
 *
 * 取样：每发生一次路由切换收集一条样本，满 3 条自动打印表格。
 *
 * ── 判读方法（每条样本的关键字段）────────────────────────────────────
 *   clickToNavMs   点击 → pathname 变化。若这项很大，说明"跳转动作本身"被阻塞。
 *   navToPaintMs   pathname 变化 → 两帧后。基线 ~33ms；上千 ms 表示主线程被堵住、
 *                  浏览器连下一帧都排不上（不是"页面画得慢"）。
 *   windowMs       本条样本的实际观察窗口（设计值 2000ms）。若明显 > 2000，
 *                  说明连 settle 的 2 秒定时器都被长任务拖后了 —— 主线程真被堵死的铁证。
 *                  重定向链（父路由立刻 redirect 到 /list）会在几十 ms 内被下一次导航
 *                  收尾，长任务来不及投递 → 会漂到目标页那条上。凡 windowMs 远小于
 *                  2000 的样本，只读它的 TabLayout 列，长任务列要去看它跳到的那个页面。
 *   longTaskTotal  观察窗口内单个 >50ms 长任务的总时长（含跨导航边界的那种，取全时长）。
 *
 *   tabCommitMs    TabLayout 各次提交耗时之和（= 提交明细ms 各值相加）。
 *   tabCommitMaxMs 单次提交耗时峰值 —— 区分"一坨慢"和"很多次小额"。
 *   提交明细ms      逐次提交耗时，'/' 分隔。如 "683/682" = 两次各 0.68s；
 *                  "490/116/97" = 第一次最贵、后两次只是连锁重渲染。
 *   tabRenderMs    各次提交中，TabLayout 组件自身 render 函数体的执行合计。
 *                  **实测常为 0.0–2.9ms** —— 这一列小意味着"慢的不是 TabLayout 自己的
 *                  代码"，而是它这次提交里挂载的子树。
 *   tabEffectMs    各次提交中，该次 commit 的 layoutEffect 自身执行合计。
 *                  ⚠️ 父组件 layoutEffect 在【所有子树 effect 之后】才跑，因此各缓存页
 *                  的 layoutEffect 已计入 commitMs，不在 effectMs 里 —— 两者不重叠。
 *   tabSyncMs      各次提交中"这一轮同步 JS 占住主线程多久"（render → 提交 → 全部
 *                  layoutEffect，用微任务探测任务结束）。这是最接近长任务口径的一项。
 *                  ⚠️ 同一次任务内若连锁发生 2 次提交，第一条的 syncMs 会把第二条一并
 *                  算进去；判读优先看 commitMs / renderMs，syncMs 只用来和最长任务对撞。
 *   domNodes       提交完成时 document 总节点数。
 *   kaEntries      提交完成时 keep-alive 缓存页签条目数（含 display:none 隐藏的）。
 *                  注：实测 commit 成本跟"新挂载那个页面自己多重"相关，跟 domNodes 总量
 *                  暂无相关 —— 用连开 15+ 页签后再回到同一路由的方式验证累积效应。
 *
 *   loaf帧数 / loaf总时长ms
 *                  LoAF（long-animation-frame）观察到的阻塞帧数与总时长。与 longTaskTotal
 *                  互为参照：LoAF 按帧统计，能把长任务归因到具体脚本。
 *   强制布局ms      阻塞帧里被【强制同步布局/样式重算】吃掉的时间（forcedStyleAndLayout-
 *                  Duration 合计）。这一项大 = 冤枉账，通常是 getBoundingClientRect /
 *                  offsetHeight 之类的量测在 DOM 变更后逼着浏览器同步重算全文档。
 *   强制布局占比%   强制布局ms / loaf总时长ms。
 *
 *   第三张表「脚本归因」= 上面这些阻塞帧里，时间到底花在哪个文件的哪个函数上。
 *   这是唯一能看见「页面自己 fetch 回来 setState 触发的重渲染」的手段 —— 那种重渲染
 *   不经过 TabLayout，既不产生提交记录也不产生长任务归属，只能靠 LoAF 抓。
 *
 *   menuPipeline   convertBackendMenusToProLayout 的耗时。基准实测应在 10ms 量级；
 *                  若这里出现几百 ms/秒级 → 菜单管线才是主因。
 */

/** 单个脚本在阻塞帧里的聚合耗时 */
type LoafScript = {
  key: string;
  /** 展示用：来源(调用方/调用类型) → 函数名 */
  from: string;
  /** 展示用：压缩后的文件路径 */
  file: string;
  duration: number;
  forced: number;
  count: number;
};

/** 一个 LoAF 帧（>50ms 且含渲染） */
type LoafEntry = {
  start: number;
  duration: number;
  scripts: {
    invoker: string;
    invokerType: string;
    sourceURL: string;
    fn: string;
    duration: number;
    forced: number;
  }[];
};

/** 单次 TabLayout 提交的明细（第二张表每次提交一行） */
type TabCommitRecord = {
  /** 本次提交发生在导航之后多久 */
  atMs: number;
  /** render 起点 → 该次 commit 的 layoutEffect 开始（原 tabCommitMs 的单次口径） */
  commitMs: number;
  /** 其中：TabLayout 组件自身 render 函数体的执行时长；-1 表示未打点 */
  renderMs: number;
  /** 该次 commit 的 layoutEffect 自身执行时长（不含子树 effect，见文件头说明） */
  effectMs: number;
  /** render 起点 → 本轮同步 JS 结束；-1 表示微任务尚未回填 */
  taskMs: number;
  /** 提交完成时 document 总节点数 */
  domNodes: number;
  /** 提交完成时缓存页签条目数 */
  kaEntries: number;
};

type Sample = {
  seq: number;
  path: string;
  prevPath: string;
  clickToNavMs: number | null;
  navToPaintMs: number | null;
  /** 本条样本实际观察窗口（重定向链下会远小于 WINDOW_MS） */
  windowMs: number;
  longTaskCount: number;
  longTaskTotalMs: number;
  longTaskMaxMs: number;
  tabCommitMs: number;
  tabCommitCount: number;
  tabCommits: TabCommitRecord[];
  loafCount: number;
  loafTotalMs: number;
  loafForcedMs: number;
  /** 按脚本聚合的阻塞时间；键为 invoker|url|fn */
  loafScripts: Record<string, LoafScript>;
  menuPipelineMs: number;
  menuPipelineCount: number;
};

const WINDOW_MS = 2000;
const AUTO_REPORT_EVERY = 3;
/** 第三张表每条样本最多列几个脚本（按耗时降序） */
const LOAF_TOP_N = 8;

let listenersReady = false;
let enabled = false;
let seq = 0;
let lastTrustedClickAt: number | null = null;
let current: (Sample & { __navAt?: number }) | null = null;
let closeTimer: number | undefined;
const samples: Sample[] = [];
const longTasks: { start: number; duration: number }[] = [];
const loafEntries: LoafEntry[] = [];

/** 本次渲染的打点：t0（函数体第一行）→ bodyEnd（return 之前） */
let pendingRender: { t0: number; bodyEnd: number | null } | null = null;

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/** 把 URL 压成可读路径：优先 /src/ 之后，其次剥掉 node_modules 前缀 */
function compactUrl(url: string): string {
  if (!url) return '(inline)';
  let u = url.replace(/^https?:\/\/[^/]+/, '');
  const srcIdx = u.indexOf('/src/');
  if (srcIdx >= 0) return u.slice(srcIdx + 1);
  const nmIdx = u.lastIndexOf('/node_modules/');
  if (nmIdx >= 0) u = u.slice(nmIdx + '/node_modules/'.length);
  const parts = u.split('/').filter(Boolean);
  return parts.length > 3 ? parts.slice(-3).join('/') : u;
}

/** 每次导航前重读一次开关，因此控制台改完 localStorage 无需刷新即可生效 */
function syncEnabled(): void {
  if (typeof window === 'undefined') return;
  try {
    enabled =
      window.localStorage.getItem('navPerf') === '1' ||
      window.location.search.includes('__navperf');
  } catch {
    enabled = false;
  }
}

function blankSample(path: string, prevPath: string): Sample & { __navAt?: number } {
  return {
    seq: ++seq,
    path,
    prevPath,
    clickToNavMs: lastTrustedClickAt == null ? null : now() - lastTrustedClickAt,
    navToPaintMs: null,
    windowMs: 0,
    longTaskCount: 0,
    longTaskTotalMs: 0,
    longTaskMaxMs: 0,
    tabCommitMs: 0,
    tabCommitCount: 0,
    tabCommits: [],
    loafCount: 0,
    loafTotalMs: 0,
    loafForcedMs: 0,
    loafScripts: {},
    menuPipelineMs: 0,
    menuPipelineCount: 0,
  };
}

/** 收尾当前样本：把窗口内产生的长任务 / LoAF 帧归集进去 */
function settle(): void {
  if (!current) return;
  const s = current;
  const navAt = s.__navAt ?? 0;
  s.windowMs = now() - navAt;
  for (const lt of longTasks) {
    // 统计与本次导航窗口「重叠」的长任务：跨边界那种（导航前开始、导航后结束）
    // 恰恰就是拖住跳转本身的元凶，只按 start 过滤会把它漏掉。
    if (lt.start + lt.duration >= navAt) {
      s.longTaskCount += 1;
      s.longTaskTotalMs += lt.duration;
      s.longTaskMaxMs = Math.max(s.longTaskMaxMs, lt.duration);
    }
  }
  longTasks.length = 0;
  for (const le of loafEntries) {
    if (le.start + le.duration < navAt) continue;
    s.loafCount += 1;
    s.loafTotalMs += le.duration;
    for (const sc of le.scripts) {
      const file = compactUrl(sc.sourceURL);
      const key = `${sc.invoker}|${sc.invokerType}|${file}|${sc.fn}`;
      const rec =
        s.loafScripts[key] ||
        {
          key,
          from: `${sc.invoker || '?'}${sc.invokerType ? `(${sc.invokerType})` : ''}${sc.fn ? ` → ${sc.fn}` : ''}`,
          file,
          duration: 0,
          forced: 0,
          count: 0,
        };
      s.loafScripts[key] = rec;
      rec.duration += sc.duration;
      rec.forced += sc.forced;
      rec.count += 1;
      s.loafForcedMs += sc.forced;
    }
  }
  loafEntries.length = 0;
  samples.push(s);
  current = null;
  pendingRender = null;
  if (samples.length % AUTO_REPORT_EVERY === 0) report();
}

function report(): void {
  if (!samples.length) {
    // eslint-disable-next-line no-console
    console.info('[navPerf] 还没有样本。先点几次菜单（需先执行 __navPerf.enable()）。');
    return;
  }
  // eslint-disable-next-line no-console
  console.table(
    samples.map((s) => ({
      路径: s.path,
      点击到跳转ms: s.clickToNavMs?.toFixed(1) ?? '-',
      跳转到绘制ms: s.navToPaintMs?.toFixed(1) ?? '-',
      观察窗口ms: s.windowMs.toFixed(0),
      长任务数: s.longTaskCount,
      长任务总计ms: s.longTaskTotalMs.toFixed(1),
      最长任务ms: s.longTaskMaxMs.toFixed(1),
      TabLayout提交ms: s.tabCommitMs.toFixed(1),
      提交次数: s.tabCommitCount,
      提交峰值ms: (s.tabCommits.length
        ? Math.max(...s.tabCommits.map((c) => c.commitMs))
        : 0
      ).toFixed(1),
      提交明细ms: s.tabCommits.map((c) => c.commitMs.toFixed(0)).join('/') || '-',
      tabRenderMs: s.tabCommits.reduce((a, c) => a + Math.max(c.renderMs, 0), 0).toFixed(1),
      tabEffectMs: s.tabCommits.reduce((a, c) => a + c.effectMs, 0).toFixed(1),
      tabSyncMs: s.tabCommits.map((c) => (c.taskMs < 0 ? '?' : c.taskMs.toFixed(0))).join('/') || '-',
      domNodes: s.tabCommits.length ? s.tabCommits[s.tabCommits.length - 1].domNodes : 0,
      kaEntries: s.tabCommits.length ? s.tabCommits[s.tabCommits.length - 1].kaEntries : 0,
      loaf帧数: s.loafCount,
      loaf总时长ms: s.loafTotalMs.toFixed(1),
      强制布局ms: s.loafForcedMs.toFixed(1),
      强制布局占比: s.loafTotalMs > 0
        ? `${((s.loafForcedMs / s.loafTotalMs) * 100).toFixed(0)}%`
        : '-',
      菜单管线ms: s.menuPipelineMs.toFixed(2),
      管线调用次数: s.menuPipelineCount,
    })),
  );

  // 第二张表：逐次提交明细 —— 判断"1 次 950ms"还是"20 次 47ms"看这张
  const detail = samples.flatMap((s) =>
    s.tabCommits.map((c, i) => ({
      路径: s.path,
      第几次: i + 1,
      距导航ms: c.atMs.toFixed(0),
      commitMs: c.commitMs.toFixed(1),
      renderMs: c.renderMs < 0 ? '?' : c.renderMs.toFixed(1),
      effectMs: c.effectMs.toFixed(1),
      syncMs: c.taskMs < 0 ? '?' : c.taskMs.toFixed(1),
      domNodes: c.domNodes,
      kaEntries: c.kaEntries,
    })),
  );
  if (detail.length) {
    // eslint-disable-next-line no-console
    console.info('[navPerf] 逐次提交明细（commitMs = render起点→layoutEffect开始）');
    // eslint-disable-next-line no-console
    console.table(detail);
  }

  // 第三张表：脚本归因 —— 阻塞时间到底花在哪个文件的哪个函数上
  const attribution = samples.flatMap((s) =>
    Object.values(s.loafScripts)
      .sort((a, b) => b.duration - a.duration)
      .slice(0, LOAF_TOP_N)
      .map((sc) => ({
        路径: s.path,
        来源: sc.from,
        文件: sc.file,
        次数: sc.count,
        合计ms: sc.duration.toFixed(1),
        强制布局ms: sc.forced.toFixed(1),
      })),
  );
  if (attribution.length) {
    // eslint-disable-next-line no-console
    console.info('[navPerf] 脚本归因（LoAF，按合计耗时降序，每条样本取前 %d）', LOAF_TOP_N);
    // eslint-disable-next-line no-console
    console.table(attribution);
  } else if (samples.some((s) => s.loafCount === 0)) {
    // eslint-disable-next-line no-console
    console.info('[navPerf] 未取到 LoAF 数据：需 Chrome 123+，且 Firefox/Safari 不支持。');
  }
}

function ensureListeners(): void {
  if (listenersReady || typeof window === 'undefined') return;
  listenersReady = true;

  // 点击时间戳始终记录：这样 enable() 之后第一次点击的 clickToNavMs 也是准的
  document.addEventListener(
    'click',
    (e) => {
      if (e.isTrusted) lastTrustedClickAt = now();
    },
    true,
  );

  try {
    const po = new PerformanceObserver((list) => {
      if (!enabled) return;
      for (const entry of list.getEntries()) {
        longTasks.push({ start: entry.startTime, duration: entry.duration });
      }
    });
    po.observe({ entryTypes: ['longtask'] });
  } catch {
    /* 浏览器不支持 longtask，其余指标仍可用 */
  }

  // LoAF：按帧给出阻塞时间，并归因到具体脚本（含强制同步布局的占比）。
  // Chrome 123+ 支持；观察者本身不产生任何业务副作用，失败即静默降级。
  try {
    const loafPo = new PerformanceObserver((list) => {
      if (!enabled) return;
      for (const raw of list.getEntries() as any[]) {
        loafEntries.push({
          start: raw.startTime,
          duration: raw.duration,
          scripts: (raw.scripts || []).map((sc: any) => ({
            invoker: String(sc.invoker ?? ''),
            invokerType: String(sc.invokerType ?? ''),
            sourceURL: String(sc.sourceURL ?? ''),
            fn: String(sc.sourceFunctionName ?? ''),
            duration: Number(sc.duration ?? 0),
            forced: Number(sc.forcedStyleAndLayoutDuration ?? 0),
          })),
        });
      }
    });
    loafPo.observe({ type: 'long-animation-frame', buffered: false } as any);
  } catch {
    /* 浏览器不支持 LoAF，前两张表仍可用 */
  }
}

/** 无论开关与否都挂上，保证 window.__navPerf 永远存在 */
function defineGlobal(): void {
  if (typeof window === 'undefined') return;
  (window as any).__navPerf = {
    get enabled() {
      return enabled;
    },
    samples,
    enable() {
      enabled = true;
      try {
        window.localStorage.setItem('navPerf', '1');
      } catch {
        /* ignore */
      }
      // eslint-disable-next-line no-console
      console.info('[navPerf] 已开始采集，请点几次菜单。自动每 3 次打印，或执行 __navPerf.dump()。');
    },
    disable() {
      enabled = false;
      try {
        window.localStorage.removeItem('navPerf');
      } catch {
        /* ignore */
      }
      // eslint-disable-next-line no-console
      console.info('[navPerf] 已停止采集。');
    },
    report,
    dump: () => JSON.stringify(samples, null, 2),
  };
}

export const navPerf = {
  get enabled(): boolean {
    return enabled;
  },

  install(): void {
    if (typeof window === 'undefined') return;
    // 生产环境不安装探针。ensureListeners 会【无条件】常驻注册两个
    // PerformanceObserver（longtask / long-animation-frame）与一个捕获阶段的
    // document click 监听，属于排查工具而非业务代码，不应在线上持续开销。
    // 开发环境（dev server）行为完全不变，仍可用 __navPerf.enable() 采集。
    // 需要在生产包上临时采数时，把下面这行注释掉重新构建即可。
    if (process.env.NODE_ENV === 'production') return;
    syncEnabled();
    ensureListeners();
    defineGlobal();
    if (enabled) {
      // eslint-disable-next-line no-console
      console.info('[navPerf] 探针已在采集（localStorage 开关为 1）。__navPerf.dump() 取结果。');
    }
  },

  /** 路由切换时调用（onRouteChange） */
  onRouteChange(path: string): void {
    syncEnabled();
    if (!enabled) return;
    const prevPath = current?.path ?? samples[samples.length - 1]?.path ?? '(none)';
    settle();
    current = blankSample(path, prevPath);
    current.__navAt = now();
    const navAt = current.__navAt;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (current && current.navToPaintMs == null) {
          current.navToPaintMs = now() - navAt;
        }
      });
    });

    clearTimeout(closeTimer);
    closeTimer = window.setTimeout(settle, WINDOW_MS);
    lastTrustedClickAt = null;
  },

  // ── TabLayout 三连打点 ─────────────────────────────────────────────
  // 用法：
  //   const mark = navPerf.tabRenderStart();   // 组件函数体第一行
  //   ...组件主体...
  //   navPerf.tabRenderEnd(mark);              // return 之前
  //   useLayoutEffect(() => {                  // 原有探针 effect 内
  //     if (mark == null) return;
  //     navPerf.tabCommit(mark, performance.now(), document.getElementsByTagName('*').length, kaEntries.length);
  //   });

  /** 渲染起点打点。未采集时返回 null，后续调用全部短路 */
  tabRenderStart(): number | null {
    if (!enabled) return null;
    const t0 = now();
    pendingRender = { t0, bodyEnd: null };
    return t0;
  },

  /** 组件函数体结束打点（return 之前），用于把 render 阶段从提交里拆出来 */
  tabRenderEnd(mark: number | null): void {
    if (mark == null || !pendingRender || pendingRender.t0 !== mark) return;
    pendingRender.bodyEnd = now();
  },

  /**
   * 布局 effect 内上报本次提交。
   * @param mark       tabRenderStart() 的返回值
   * @param effectStart 该 layoutEffect 开始时刻
   * @param domNodes   此刻 document 总节点数
   * @param kaEntries  此刻缓存页签条目数
   */
  tabCommit(mark: number | null, effectStart: number, domNodes: number, kaEntries: number): void {
    if (mark == null || !enabled || !current) return;
    const bodyEnd = pendingRender && pendingRender.t0 === mark ? pendingRender.bodyEnd : null;
    const rec: TabCommitRecord = {
      atMs: now() - (current.__navAt ?? 0),
      commitMs: effectStart - mark,
      renderMs: bodyEnd == null ? -1 : bodyEnd - mark,
      effectMs: now() - effectStart,
      taskMs: -1,
      domNodes,
      kaEntries,
    };
    current.tabCommitMs += rec.commitMs;
    current.tabCommitCount += 1;
    current.tabCommits.push(rec);
    // 微任务在当前任务栈排空时执行 → 量到「render + 提交 + 全部 layoutEffect」这一整段同步 JS。
    // 即使样本已被 settle，rec 仍挂在数组里，回填可见。
    queueMicrotask(() => {
      rec.taskMs = now() - mark;
    });
  },

  /** 菜单转换耗时 */
  menuPipeline(ms: number): void {
    if (!enabled || !current) return;
    current.menuPipelineMs += ms;
    current.menuPipelineCount += 1;
  },
};

export function installNavPerfProbe(): void {
  navPerf.install();
}